<template>
    <Modal id="online-status-modal"
           ref="modal"
           :title="title"
           :icon="icon"
           :cancel="resolve"
           cancel-label="CLOSE">
        <div class="mb-1 btn-group btn-group-justified">
            <label class="btn btn-default"
                   :class="{'active': selectedTab === 'pending'}"
                   @click.prevent="selectedTab = 'pending'">
                {{ l('ONLINE_STATUS.PENDING_TASKS') }}
                <i v-if="numberPendingTasks === null"
                   class="fa-solid fa-spinner fa-spin" />
                <span v-if="numberPendingTasks"
                      class="label">{{ numberFilter(numberPendingTasks) }}</span>
            </label>
            <label class="btn btn-default"
                   :class="{'active': selectedTab === 'failed'}"
                   @click.prevent="selectedTab = 'failed'">
                {{ l('ONLINE_STATUS.FAILED_TASKS') }}
                <i v-if="numberFailedTasks === null"
                   class="fa-solid fa-spinner fa-spin" />
                <span v-else-if="numberFailedTasks"
                      class="label">{{ numberFilter(numberFailedTasks) }}</span>
            </label>
        </div>

        <div v-if="selectedTab === 'pending'">
            <div v-if="numberPendingTasks < 1"
                 class="alert alert-success alert--small mb-05">
                {{ l('ONLINE_STATUS.NO_PENDING_TASKS') }}
            </div>
            <div v-else
                 class="alert alert-info alert--small mb-05">
                {{ l('ONLINE_STATUS.PENDING_TASKS_DESC') }}
            </div>

            <OnlineTaskList v-if="numberPendingTasks !== null && numberPendingTasks > 0"
                            ref="pending-online-task-list"
                            :tasks="pendingTasks"
                            active-tab="pending"
                            @scroll-to-top="scrollToTop" />
        </div>
        <div v-else-if="selectedTab === 'failed'">
            <div v-if="numberFailedTasks !== null && numberFailedTasks < 1"
                 class="alert alert-success alert--small mb-05">
                {{ l('ONLINE_STATUS.NO_FAILED_TASKS') }}
            </div>
            <div v-else
                 class="alert alert-danger alert--small mb-05">
                {{ l('ONLINE_STATUS.FAILED_TASKS_DESC') }}
            </div>

            <OnlineTaskList v-if="numberFailedTasks !== null && numberFailedTasks > 0"
                            ref="failed-online-task-list"
                            :tasks="failedTasks"
                            active-tab="failed"
                            @clear-failed-tasks="clearFailedTasks"
                            @clear-failed-task="clearFailedTask($event)"
                            @scroll-to-top="scrollToTop" />
        </div>
    </Modal>
</template>

<script>
import EventNames from '@/lib/Events/EventNames';
import Modal from '@/vue/components/Modals/Modal';
import OnlineTaskList from './OnlineTaskList';
import { container } from '@/container';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { timeoutPromise } from '@/functions/functions';

export default {
    name: 'OnlineStatusModal',

    components: {
        Modal,
        OnlineTaskList,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {},

    data() {
        return {
            isOnline: null,
            numberPendingTasks: null,
            numberFailedTasks: null,
            pendingTasks: [],
            failedTasks: [],
            selectedTab: 'pending',
        };
    },

    computed: {
        taskCount() {
            return this.tasks.length;
        },

        icon() {
            return this.isOnline ? 'fa-wifi' : 'fa-wifi-slash';
        },

        title() {
            return this.isOnline ? 'ONLINE_STATUS.MODAL_TITLE__ONLINE' : 'ONLINE_STATUS.MODAL_TITLE__OFFLINE';
        },

        tasks() {
            return this.selectedTab === 'pending' ? this.pendingTasks : this.failedTasks;
        },
    },

    created() {
        this.isOnline = container.connectivityChecker.isOnline();
        this.deregisterOnlineStatusChangedListenerFunc = container.$rootScope.$on(
            EventNames.ONLINE_STATUS_CHANGED,
            () => {
                this.isOnline = container.connectivityChecker.isOnline();
            }
        );

        this.loadTasks();
        this.deregisterOnlineQueueModifiedListenerFunc = container.$rootScope.$on(
            EventNames.ONLINE_QUEUE_MODIFIED,
            () => {
                this.loadTasks();
            }
        );
    },

    beforeUnmount() {
        if (this.deregisterOnlineQueueModifiedListenerFunc) {
            this.deregisterOnlineQueueModifiedListenerFunc();
        }
        if (this.deregisterOnlineStatusChangedListenerFunc) {
            this.deregisterOnlineStatusChangedListenerFunc();
        }
    },

    methods: {
        loadTasks() {
            // Get counts of our tasks very quickly.
            container.onlineTaskRepository.countAllCurrent()
                .then((c) => {
                    this.numberPendingTasks = c;
                });
            container.onlineTaskRepository.countFailed()
                .then((c) => {
                    this.numberFailedTasks = c;
                });
            // Then fetch the actual tasks which can take a small but noticeable time with a large queue.
            container.onlineTaskRepository.findAllCurrent()
                .then((tasks) => {
                    this.pendingTasks = tasks;
                });
            container.onlineTaskRepository.findAllFailed()
                .then((tasks) => {
                    this.failedTasks = tasks;
                });
        },

        async clearFailedTasks() {
            // Having a tiny delay here makes it feel less jarring.
            await timeoutPromise(500);
            this.failedTasks.forEach((task) => this.clearFailedTask(task.ID));
        },

        async clearFailedTask(taskID) {
            // Having a tiny delay here makes it feel less jarring.
            await timeoutPromise(500);
            container.onlineTaskRepository.remove(taskID)
                .then(() => {});
        },

        getTaskListOffsetTop() {
            return this.$refs.modal.$el.querySelector('.online-task-list').offsetTop;
        },

        scrollToTop() {
            this.$refs.modal.scrollBodyTo(this.getTaskListOffsetTop());
        },
    },
};
</script>

<style lang="less">
@import "../../../less/variables";

#online-status-modal {
    .table {
        th, td {
            font-size: @font-size-small;
        }
    }
}
</style>
